import React, { useState, useEffect, useCallback } from 'react';
import EmailCollectionForm from './EmailCollectionForm';
import EmailVerificationScreen from '../auth/EmailVerificationScreen';
import AccountCompletionForm from '../auth/AccountCompletionForm';
import './WaitlistFlowContainer.css';

const FLOW_STEPS = {
  EMAIL_COLLECTION: 'email-collection',
  EMAIL_VERIFICATION: 'email-verification',
  ACCOUNT_COMPLETION: 'account-completion',
  SUCCESS: 'success'
};

const STORAGE_KEY = 'waitlistFlow';

const WaitlistFlowContainer = ({ onFlowComplete, onError }) => {
  const [currentStep, setCurrentStep] = useState(FLOW_STEPS.EMAIL_COLLECTION);
  const [userData, setUserData] = useState({
    email: '',
    linkedinUrl: ''
  });
  const [verificationData, setVerificationData] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);

  // Load flow state from session storage on mount
  useEffect(() => {
    const savedFlow = sessionStorage.getItem(STORAGE_KEY);
    if (savedFlow) {
      try {
        const flowData = JSON.parse(savedFlow);
        const now = Date.now();
        
        // Check if session is still valid (within 30 minutes)
        if (flowData.lastActivity && (now - flowData.lastActivity) < 30 * 60 * 1000) {
          // Validate the flow data structure
          if (flowData.currentStep && Object.values(FLOW_STEPS).includes(flowData.currentStep)) {
            setCurrentStep(flowData.currentStep);
            setUserData(flowData.userData || { email: '' });
            setVerificationData(flowData.verificationData || null);
            
            // Track flow recovery for analytics
            if (typeof window.gtag !== 'undefined') {
              window.gtag('event', 'waitlist_flow_recovered', {
                step: flowData.currentStep,
                time_elapsed: Math.round((now - flowData.startedAt) / 1000)
              });
            }
          } else {
            // Invalid flow data, start fresh
            sessionStorage.removeItem(STORAGE_KEY);
          }
        } else {
          // Session expired, clear storage and track abandonment
          sessionStorage.removeItem(STORAGE_KEY);
          
          if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'waitlist_flow_expired', {
              step: flowData.currentStep,
              time_elapsed: Math.round((now - flowData.startedAt) / 1000)
            });
          }
        }
      } catch (error) {
        console.error('Error loading flow state:', error);
        sessionStorage.removeItem(STORAGE_KEY);
        
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'waitlist_flow_recovery_error', {
            error: error.message
          });
        }
      }
    }
  }, []);

  // Session expiration warning
  useEffect(() => {
    if (currentStep !== FLOW_STEPS.EMAIL_COLLECTION && currentStep !== FLOW_STEPS.SUCCESS) {
      const checkSessionExpiration = () => {
        const savedFlow = sessionStorage.getItem(STORAGE_KEY);
        if (savedFlow) {
          try {
            const flowData = JSON.parse(savedFlow);
            const now = Date.now();
            const timeElapsed = now - flowData.lastActivity;
            const warningTime = 25 * 60 * 1000; // 25 minutes (5 minutes before expiration)
            
            if (timeElapsed > warningTime && !sessionWarning) {
              setSessionWarning(true);
            }
          } catch (error) {
            console.error('Error checking session expiration:', error);
          }
        }
      };

      const interval = setInterval(checkSessionExpiration, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [currentStep, sessionWarning]);

  // Save flow state to session storage
  const saveFlowState = useCallback((step, data, verification) => {
    const flowState = {
      currentStep: step,
      userData: data,
      verificationData: verification,
      startedAt: Date.now(),
      lastActivity: Date.now(),
      version: '1.0' // For future migration compatibility
    };
    
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(flowState));
      
      // Track flow progress for analytics
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'waitlist_flow_step', {
          step: step,
          email: data?.email ? 'provided' : 'not_provided'
        });
      }
    } catch (error) {
      console.error('Error saving flow state:', error);
      // Continue without persistence rather than breaking the flow
    }
  }, []);

  // Smooth transition between steps
  const transitionToStep = useCallback((newStep, newUserData = null, newVerificationData = null) => {
    setIsTransitioning(true);
    
    setTimeout(() => {
      setCurrentStep(newStep);
      if (newUserData) setUserData(newUserData);
      if (newVerificationData) setVerificationData(newVerificationData);
      
      // Save state after transition
      saveFlowState(newStep, newUserData || userData, newVerificationData || verificationData);
      
      setTimeout(() => {
        setIsTransitioning(false);
      }, 150);
    }, 150);
  }, [userData, verificationData, saveFlowState]);

  // Handle email collection success
  const handleEmailSubmissionSuccess = useCallback((submissionData) => {
    const newUserData = {
      email: submissionData.email,
      linkedinUrl: submissionData.linkedinUrl
    };
    
    const newVerificationData = {
      verificationId: submissionData.verificationId,
      email: submissionData.email
    };

    transitionToStep(FLOW_STEPS.EMAIL_VERIFICATION, newUserData, newVerificationData);
  }, [transitionToStep]);

  // Handle email verification success
  const handleVerificationSuccess = useCallback((verifiedData) => {
    transitionToStep(FLOW_STEPS.ACCOUNT_COMPLETION, userData, {
      ...verificationData,
      isVerified: true,
      verifiedAt: Date.now()
    });
  }, [transitionToStep, userData, verificationData]);

  // Handle account completion success
  const handleAccountCreationSuccess = useCallback((accountData) => {
    // Update userData with the complete account information
    const completeUserData = {
      ...userData,
      firstName: accountData.firstName,
      lastName: accountData.lastName,
      ...accountData
    };
    
    transitionToStep(FLOW_STEPS.SUCCESS, completeUserData);
    
    // Track successful completion
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'waitlist_signup_complete', {
        email: completeUserData.email ? 'provided' : 'not_provided',
        has_linkedin: completeUserData.linkedinUrl ? 'yes' : 'no'
      });
    }
    
    // Clear session storage after successful completion
    setTimeout(() => {
      sessionStorage.removeItem(STORAGE_KEY);
    }, 1000);
    
    // Notify parent component
    if (onFlowComplete) {
      onFlowComplete(completeUserData);
    }
  }, [transitionToStep, userData, onFlowComplete]);

  // Handle back navigation
  const handleGoBack = useCallback(() => {
    switch (currentStep) {
      case FLOW_STEPS.EMAIL_VERIFICATION:
        transitionToStep(FLOW_STEPS.EMAIL_COLLECTION);
        break;
      case FLOW_STEPS.ACCOUNT_COMPLETION:
        transitionToStep(FLOW_STEPS.EMAIL_VERIFICATION);
        break;
      default:
        break;
    }
  }, [currentStep, transitionToStep]);

  // Handle errors
  const handleError = useCallback((error) => {
    console.error('Waitlist flow error:', error);
    
    // Track errors for analytics
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'waitlist_flow_error', {
        step: currentStep,
        error: typeof error === 'string' ? error : error.message || 'Unknown error'
      });
    }
    
    if (onError) {
      onError(error);
    }
  }, [onError, currentStep]);

  // Restart flow
  const handleRestartFlow = useCallback(() => {
    // Track flow restart
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'waitlist_flow_restart', {
        from_step: currentStep
      });
    }
    
    sessionStorage.removeItem(STORAGE_KEY);
    setCurrentStep(FLOW_STEPS.EMAIL_COLLECTION);
    setUserData({ email: '', linkedinUrl: '' });
    setVerificationData(null);
    setIsTransitioning(false);
  }, [currentStep]);

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case FLOW_STEPS.EMAIL_COLLECTION:
        return (
          <EmailCollectionForm
            onSubmissionSuccess={handleEmailSubmissionSuccess}
            onError={handleError}
            initialData={userData}
          />
        );

      case FLOW_STEPS.EMAIL_VERIFICATION:
        return (
          <EmailVerificationScreen
            email={userData.email}
            verificationId={verificationData?.verificationId}
            onVerificationSuccess={handleVerificationSuccess}
            onGoBack={handleGoBack}
            onError={handleError}
          />
        );

      case FLOW_STEPS.ACCOUNT_COMPLETION:
        return (
          <AccountCompletionForm
            verifiedEmail={userData.email}
            linkedinUrl={userData.linkedinUrl}
            onSubmissionSuccess={handleAccountCreationSuccess}
            onGoBack={handleGoBack}
            onError={handleError}
          />
        );

      case FLOW_STEPS.SUCCESS:
        return (
          <div className="waitlist-flow-success">
            <div className="success-icon">✓</div>
            <h2>Welcome to the waitlist!</h2>
            <p>
              Thank you for joining{userData.firstName ? `, ${userData.firstName}` : ''}! We've created your account 
              and you can now log in. You'll be notified when we're ready to welcome you to the full platform.
            </p>
            <div className="success-actions">
              <button 
                className="btn btn-primary"
                onClick={() => window.location.href = '/login'}
              >
                Log In
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => window.location.reload()}
              >
                Continue
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Calculate progress percentage
  const getProgressPercentage = () => {
    switch (currentStep) {
      case FLOW_STEPS.EMAIL_COLLECTION:
        return 33;
      case FLOW_STEPS.EMAIL_VERIFICATION:
        return 66;
      case FLOW_STEPS.ACCOUNT_COMPLETION:
        return 100;
      case FLOW_STEPS.SUCCESS:
        return 100;
      default:
        return 0;
    }
  };

  return (
    <div className="waitlist-flow-container">
      {/* Progress Bar */}
      {currentStep !== FLOW_STEPS.EMAIL_COLLECTION && currentStep !== FLOW_STEPS.SUCCESS && (
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div 
              className="progress-bar-fill"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
        </div>
      )}
      
      <div className={`waitlist-flow-content ${isTransitioning ? 'transitioning' : ''}`}>
        {renderCurrentStep()}
      </div>

      {/* Session expiration warning */}
      {sessionWarning && (
        <div className="session-warning">
          <div className="session-warning-content">
            <p className="session-warning-text">
              Your session will expire soon. Continue to keep your progress.
            </p>
            <div className="session-warning-actions">
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setSessionWarning(false);
                  // Update last activity to extend session
                  saveFlowState(currentStep, userData, verificationData);
                }}
              >
                Continue
              </button>
              <button 
                className="btn btn-link btn-sm"
                onClick={() => setSessionWarning(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default WaitlistFlowContainer;